'use client';

import { Accordion as AccordionPrimitive } from '@base-ui/react/accordion';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDownIcon } from 'lucide-react';

// gloss
const accordionVariants = cva('flex w-full flex-col', {
  variants: {
    variant: {
      card: 'overflow-hidden rounded-md border',
      plain: '',
    },
  },
  defaultVariants: { variant: 'card' },
});

const accordionItemVariants = cva('', {
  variants: {
    variant: {
      card: 'not-last:border-b data-open:bg-muted/50',
      plain: '',
    },
  },
  defaultVariants: { variant: 'card' },
});

type AccordionProps = AccordionPrimitive.Root.Props & VariantProps<typeof accordionVariants>;

const Accordion = ({ className, variant, ...props }: AccordionProps) => {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={cn(accordionVariants({ variant, className }))}
      {...props}
    />
  );
};

type AccordionItemProps = AccordionPrimitive.Item.Props & VariantProps<typeof accordionItemVariants>;

const AccordionItem = ({ className, variant, ...props }: AccordionItemProps) => {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(accordionItemVariants({ variant, className }))}
      {...props}
    />
  );
};

type AccordionTriggerProps = AccordionPrimitive.Trigger.Props & {
  // why: the trigger's <h3> wrapper is what sits in the parent's layout — sizing it is the only way
  // why: to put a sibling beside the trigger rather than inside it.
  headerClassName?: string;
};

const AccordionTrigger = ({ className, children, headerClassName, ...props }: AccordionTriggerProps) => {
  return (
    <AccordionPrimitive.Header className={cn('flex', headerClassName)}>
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'group/accordion-trigger flex flex-1 cursor-pointer items-center justify-between gap-4 p-4 text-left font-medium text-sm outline-none transition-all hover:underline aria-disabled:pointer-events-none aria-disabled:opacity-50',
          className,
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon
          data-slot="accordion-trigger-icon"
          className="pointer-events-none size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-aria-expanded/accordion-trigger:rotate-180"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
};

const AccordionContent = ({ className, children, ...props }: AccordionPrimitive.Panel.Props) => {
  return (
    <AccordionPrimitive.Panel
      data-slot="accordion-content"
      className="overflow-hidden text-sm data-closed:animate-accordion-up data-open:animate-accordion-down"
      {...props}
    >
      <div className={cn('px-4 pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Panel>
  );
};

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, accordionItemVariants, accordionVariants };
